import React, { Fragment, useState } from "react";
import { useFormikContext } from "formik";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import Overlay from "components/Overlay";
import CombinationCheckoutBuilder from "../CombinationCheckoutBuilder";
import CombinationPreview from "../CombinationPreview";
import { STATE_KEYS } from "utils/constants/state";
import { simulateFormSelections } from "utils/form";
import { getClearSelections } from "utils/combination";

const CHECKOUT_PICKER_INTENT = {
  SELECT_RESULT: "SELECT_RESULT"
};

const CombinationShow = ({ name, activeIndex, combinations }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const fieldGroupsPath = `${name}.${STATE_KEYS.FORM.FIELD_GROUPS}`;
  const combinationPath = `${name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_COMBOS}[${activeIndex}]`;
  const setResultPath = `${combinationPath}.result`;

  const previousCombination = combinations[activeIndex - 1];

  const fieldGroups = get(values, fieldGroupsPath);
  const combination = get(values, combinationPath);

  /**
   * Allow the user to toggle between product and price pick intent
   */
  const [intent, setIntent] = useState(null);
  let overlayContent;
  const showOverlay = Boolean(intent);

  /**
   * Preview
   * - set form data values to be the key:value uuid pairs for selected fields and their values
   * - esentially simulating the form selections
   */
  const selections = combination && combination.selections;
  const setPreview = simulateFormSelections({
    values,
    name: fieldGroupsPath,
    setFieldValue,
    selections
  });
  /**
   * Clear preview
   * - unset all the selection field uuid values
   */
  const clearedSelections = getClearSelections(selections);
  const clearPreview = simulateFormSelections({
    values,
    name: fieldGroupsPath,
    setFieldValue,
    selections: clearedSelections
  });

  const hasComboSelections = !isEmpty(combination && combination.selections);
  const hasComboResult = !isEmpty(combination && combination.result);
  const hasPreviousComboResult = !isEmpty(
    previousCombination && previousCombination.result
  );
  const canPreview = hasComboResult && hasComboSelections;

  const clearResult = () => setResult({});
  const clonePrevious = () => {
    setResult(previousCombination.result);
  };

  /**
   * 1. Set the checkout picker values as the combination.result
   * 2. Touch the state
   * 3. Hide the Overlay
   * 4. Simulate selection in the form
   * @param {Object} combinationResult
   */
  const setResult = (combinationResult) => {
    // 1.
    setFieldValue(setResultPath, combinationResult);

    // 2.
    const isTouched = get(touched, setResultPath);
    if (!isTouched) {
      setFieldTouched(setResultPath, true);
    }

    // 3.
    setIntent(null);

    // 4.
    setTimeout(() => {
      setPreview();
    }, 0);
  };

  if (intent === CHECKOUT_PICKER_INTENT.SELECT_RESULT) {
    overlayContent = (
      <CombinationCheckoutBuilder
        name={name}
        combination={combination}
        activeIndex={activeIndex}
        goBack={setResult}
      />
    );
  }

  const previewProps = {
    name,
    onClick: () => setIntent(CHECKOUT_PICKER_INTENT.SELECT_RESULT),
    fieldGroups,
    combination,
    clearResult: hasComboResult ? clearResult : null,
    cloneResult: hasPreviousComboResult ? clonePrevious : null,
    canPreview,
    setPreview,
    clearPreview
  };

  return (
    <Fragment>
      {combination && <CombinationPreview {...previewProps} />}
      {showOverlay && overlayContent && (
        <Overlay hideOverlay={() => setIntent(null)}>{overlayContent}</Overlay>
      )}
    </Fragment>
  );
};

export default CombinationShow;
